/**
 * The phone's Back — Android's back button / gesture, iOS Safari's edge swipe — closes whatever
 * is open on top (a sheet, the chat, a dialog) instead of leaving the page. That is what people
 * expect from an app; without it, Back throws away the screen they were working on.
 *
 * How: every open layer pushes one extra history entry for the SAME url, carrying the router's
 * own state plus a token (so react-router sees no navigation and its index stays right). Back
 * pops that entry → `popstate` → the layer closes. A layer closed by its own UI (×, backdrop,
 * swipe) takes its entry back off with history.back() — unless the app already navigated on top
 * of it, in which case the entry just stays behind (it is the same page).
 *
 * Navigating FROM a layer: call `navigateFromLayer` (or check `layerOnTop()` and pass
 * `replace`), so the new page replaces the layer's entry instead of stacking on it — one Back
 * then returns to where the person came from.
 */

type Layer = { token: string; close: () => void };

const stack: Layer[] = [];
let seq = 0;
let pendingPops = 0;
let waiting: Array<() => void> = [];
let flushTimer = 0;
let listening = false;
const SESSION = Date.now().toString(36);
/** A closed layer whose entry is about to be taken off (history.back() is sent a tick later). */
let orphan: { token: string; timer: number } | null = null;

const tokenOnTop = (): string | null => {
  const st = window.history.state as { mLayer?: string } | null;
  return (st && st.mLayer) || null;
};

const flush = () => {
  window.clearTimeout(flushTimer);
  pendingPops = 0;
  const fns = waiting;
  waiting = [];
  fns.forEach((fn) => fn());
};

const onPop = () => {
  if (pendingPops > 0) pendingPops -= 1;
  // Close every layer above the entry we have landed on (usually just the top one).
  const now = tokenOnTop();
  while (stack.length > 0 && stack[stack.length - 1].token !== now) {
    const layer = stack.pop()!;
    layer.close();
  }
  // Landed on the entry of a layer that is already gone — it closed while something sat on top
  // of it (a sheet that opened the chat), or the page was reloaded. It is the same page as the
  // entry below, so step over it: one Back should always do something visible.
  if (now && !stack.some((l) => l.token === now) && !(orphan && orphan.token === now)) {
    pendingPops += 1;
    window.history.back();
    return;
  }
  if (pendingPops === 0 && waiting.length > 0) flush();
};

/** Register an open layer. Returns `dismiss`, to call when the layer closes by itself. */
export function openLayer(close: () => void): () => void {
  if (!listening) {
    window.addEventListener('popstate', onPop);
    listening = true;
  }
  seq += 1;
  // Unique across reloads too: entries from before a reload are still in the history.
  const token = `l${SESSION}-${seq}`;
  const state = { ...(window.history.state || {}), mLayer: token };
  if (orphan && tokenOnTop() === orphan.token) {
    // A layer closed and another opened in the same tick (React StrictMode does exactly this
    // on mount): take over its entry instead of stacking a second one.
    window.clearTimeout(orphan.timer);
    orphan = null;
    pendingPops = Math.max(0, pendingPops - 1);
    window.history.replaceState(state, '');
  } else {
    window.history.pushState(state, '');
  }
  stack.push({ token, close });

  return () => {
    const i = stack.findIndex((l) => l.token === token);
    if (i === -1) return; // already closed by Back
    stack.splice(i, 1);
    if (tokenOnTop() !== token) return; // something is on top of our entry: leave it be
    pendingPops += 1;
    // A tick later, so a navigation made right after closing (same click) wins over the Back.
    const timer = window.setTimeout(() => {
      if (orphan && orphan.token === token) orphan = null;
      if (tokenOnTop() === token) {
        window.history.back();
        // A Back that never lands (it always should) must not hold queued work forever.
        window.clearTimeout(flushTimer);
        flushTimer = window.setTimeout(flush, 400);
      } else {
        pendingPops = Math.max(0, pendingPops - 1);
        if (pendingPops === 0 && waiting.length > 0) flush();
      }
    }, 0);
    orphan = { token, timer };
  };
}

/** True while a layer's history entry is the current one. */
export const layerOnTop = (): boolean => tokenOnTop() !== null;

/** Run `fn` once the Back that a just-closed layer triggered has landed (or right away). */
export function afterLayerClosed(fn: () => void) {
  if (pendingPops > 0) waiting.push(fn);
  else fn();
}

/** Navigate from inside a layer: the new page takes the layer's history entry. */
export function navigateFromLayer(navigate: (to: string, opts?: { replace?: boolean }) => void, to: string) {
  navigate(to, { replace: layerOnTop() });
}
